import { AISummary } from '@statsbomb/kitbag-ai-summary';
import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import { playerIdAtom } from '@/atoms/player/player';
import {
  PlayerAiSummaryFilterParams,
  playerAiSummaryFilterParamsAtom,
} from '@/atoms/queries/player/playerAiSummaryFilters';
import { unwrappedSelectedSummaryProfileAtom } from '@/atoms/radar';
import { NoTextLoader } from '@/components/contentState/NoTextLoader';
import { PlayerSummaryData, usePlayerFilteredSummaryData } from '@/hooks/usePlayerFilteredSummaryData';
import { isNullish } from '@/utils/predicates';
import { toProfileInfo } from '@/utils/radar';

const unwrappedPlayerAiSummaryFilterParamsAtom = unwrap(playerAiSummaryFilterParamsAtom, () => undefined);

const buildPlayerData = (
  playerId: number,
  summaryData: PlayerSummaryData,
  filterParams: PlayerAiSummaryFilterParams,
  profileInfo: ReturnType<typeof toProfileInfo>,
) => ({
  playerId: String(playerId),
  metrics: summaryData.metrics,
  minutesPlayed: summaryData.minutesPlayed,
  primaryPosition: summaryData.primaryPosition,
  positions: summaryData.positions,
  ...(!isNullish(summaryData.cohortSize) ? { cohortSize: summaryData.cohortSize } : {}),
  competitionInfos: filterParams.competitionInfos.map(({ id, name, country }) => ({
    id: String(id),
    name,
    country,
  })),
  seasonInfos: filterParams.seasonInfos.map(({ id, name }) => ({
    id: String(id),
    name,
  })),
  profileInfo,
});

export const PlayerDashboardSummaryContent = () => {
  const { t } = useTranslation('radar');
  const playerId = useAtomValue(playerIdAtom);
  const filterParams = useAtomValue(unwrappedPlayerAiSummaryFilterParamsAtom);
  const summaryData = usePlayerFilteredSummaryData();
  const selectedProfile = useAtomValue(unwrappedSelectedSummaryProfileAtom);

  const isLoading = filterParams === undefined || summaryData === undefined || selectedProfile === undefined;
  if (isLoading) return <NoTextLoader />;
  if (!filterParams)
    throw new Error('playerAiSummaryFilterParamsAtom resolved to null: no player cycles match selected filters');
  if (!selectedProfile)
    throw new Error('selectedSummaryProfileAtom resolved to null: no summary profile matches the selected id');
  if (isNullish(playerId)) throw new Error('playerIdAtom resolved to null: cannot render summary without a player id');

  const profileInfo = toProfileInfo(selectedProfile, t, summaryData.positionMinutes, summaryData.hiddenMetricKeys);
  const playerData = buildPlayerData(playerId, summaryData, filterParams, profileInfo);

  return (
    <div className="h-full overflow-y-auto">
      {/* There is pre-existing custom styling within this project. Follow up required to handle styling inside component library */}
      <AISummary key={playerData.playerId} playerData={playerData} />
    </div>
  );
};
